import {
    PRISMIC_CUSTOM_TYPE,
    PRISMIC_SPAN_LINK_TYPE,
    PRISMIC_SPAN_TYPE,
} from 'utils/prismic-types';
import DestinationAPI from 'api/destination';
import CityAPI from 'api/city';
import AirportAPI from 'api/airport';
import {
    interpolateNestedValue,
    interpolatePageMeta,
} from 'cms/utils/interpolate-utils';

const fetchLinksApiData = parsedLinks => {
    return Promise.all(
        parsedLinks
            .map(({slug, type}) => {
                if (type === PRISMIC_CUSTOM_TYPE.DESTINATION_MIDFUNNEL)
                    return DestinationAPI.get(slug).then(res => ({
                        slug,
                        type,
                        data: res.data.data,
                    }));
                if (type === PRISMIC_CUSTOM_TYPE.CITY_MIDFUNNEL)
                    return CityAPI.get(slug).then(res => ({
                        slug,
                        type,
                        data: res.data.data,
                    }));
                if (type === PRISMIC_CUSTOM_TYPE.AIRPORT_MIDFUNNEL)
                    return AirportAPI.getByCode(slug).then(res => ({
                        slug,
                        type,
                        data: res.data.data,
                    }));

                return null;
            })
            .filter(p => Boolean(p))
    );
};

const transformToLinksMap = linksData => {
    /**
     * Format city for URL. Replaces spaces with hyphens.
     *
     * @param {string} city - City title
     * @returns {string} City formatted for URL
     */
    const formatCity = city => city.replace(/\s+/g, '-');

    return linksData.reduce((acc, {slug, type, data}) => {
        const key = `${slug}-${type}`.toLowerCase();
        const {city, relative_url: relativeUrl} = data;

        // url: /destination/<city>/<relative_url>
        // Note: rely on relative_url from api
        if (type === PRISMIC_CUSTOM_TYPE.DESTINATION_MIDFUNNEL) {
            acc[key] = `/destination/${formatCity(
                city
            )}/${relativeUrl}`.toLowerCase();
        }

        // url: /city/<relative_url>
        // Note: rely on relative_url from api
        if (type === PRISMIC_CUSTOM_TYPE.CITY_MIDFUNNEL) {
            acc[key] = `/city/${relativeUrl}`.toLowerCase();
        }

        // url: /airport/<city>/<iata-code>
        // Note: Prismic slug is iata code
        if (type === PRISMIC_CUSTOM_TYPE.AIRPORT_MIDFUNNEL) {
            acc[key] = `/airport/${formatCity(
                city
            )}/${slug}-parking`.toLowerCase();
        }

        return acc;
    }, {});
};

const extractDocumentLinks = nestedValue => {
    if (nestedValue?.spans?.length) {
        return nestedValue.spans
            .filter(
                ({type, data}) =>
                    type === PRISMIC_SPAN_TYPE.HYPERLINK &&
                    data?.link_type === PRISMIC_SPAN_LINK_TYPE.DOCUMENT
            )
            .map(({data: {slug, type}}) => ({slug, type}));
    }

    return [];
};

/**
 * Transform Prismic Document links into Prismic Web links with resolved URL
 *
 * @param {object} nestedValue - Prismic page object
 * @param {object} links - links map
 * @returns {object} Transformed Prismic page object
 */
const transformLinksInNestedPageObject = (nestedValue, links) => {
    if (nestedValue?.spans?.length) {
        const spans = nestedValue.spans.map(span => {
            const {type: spanType, data} = span;

            if (
                spanType === PRISMIC_SPAN_TYPE.HYPERLINK &&
                data?.link_type === PRISMIC_SPAN_LINK_TYPE.DOCUMENT
            ) {
                const {
                    data: {slug, type},
                } = span;
                const key = `${slug}-${type}`.toLowerCase();
                const url = links[key];

                // Transform Document link into Web link
                if (links[key]) {
                    return {
                        ...span,
                        data: {
                            ...span.data,
                            url,
                            // eslint-disable-next-line camelcase
                            link_type: PRISMIC_SPAN_LINK_TYPE.WEB,
                        },
                    };
                }
            }

            return span;
        });

        return {...nestedValue, spans};
    }

    return nestedValue;
};

/**
 * Interpolate text and transform Document links in Prismic page object
 *
 * @param {object} page - Prismic page object
 * @param {object} interpolations - interpolations map
 * @returns {object} Interpolated Prismic page object
 */
export const interpolatePrismicPage = async (page, interpolations) => {
    const extractedLinks = [];

    // extract all Document links
    JSON.stringify(page, (_, value) => {
        extractedLinks.push(...extractDocumentLinks(value));

        return value;
    });

    // fetch destination/city/airport data needed to build URLs
    // build a links map (key: slug-type, value: URL)
    const linksMap = transformToLinksMap(
        await fetchLinksApiData(extractedLinks)
    );

    // interpolate text and transform links
    const json = JSON.stringify(
        interpolatePageMeta(page, interpolations),
        (_, value) => {
            const nestedValue = transformLinksInNestedPageObject(
                value,
                linksMap
            );

            return interpolateNestedValue(nestedValue, interpolations);
        }
    );

    return JSON.parse(json);
};
